<script setup lang="ts">
// @ts-nocheck
import { ImageComponent } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import ActivityAvatar from './ActivityAvatar.vue'

import DeletedIcon from '@/assets/icons/delete.svg?component'
import SystemIcon from '@/assets/icons/settings.svg?component'
import { getParticipantAvatarAbbreviation } from '@/components/CampaignParticipants/CampaignParticipantDetails/CampaignParticipantDetails.helpers'
import { STATUS_TITLE_MAP } from '@/components/CampaignParticipants/CampaignParticipantsColumns.helpers'
import { normalizeImage } from '@/lib/adapters/image.adapters.ts'
import { formatDateTimeLong } from '@/lib/formatters'
import type {
  ActivityDeletedCommentType,
  ActivityStatusChangeType,
  ActivityStatusType,
  CampaignParticipantType,
} from '@/types/campaignParticipant.types'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  activity: ActivityDeletedCommentType | ActivityStatusChangeType
  participant: CampaignParticipantType
  index: number
}>()
const createdDate = computed(() => formatDateTimeLong(props.activity.created_at))

const isParticipantAvatarErrorLoad = ref(false)
const participantAvatar = computed(() => (props.participant.creator_avatar ? normalizeImage(props.participant.creator_avatar) : null))
const avatarColor = computed(() => (props.index % 2 === 0 ? '#afecc4' : '#ecafaf'))
const avatarAbbreviation = computed(() =>
  props.activity.actor_display_name ? getParticipantAvatarAbbreviation(props.activity.actor_display_name) : '',
)

const errorParticipantAvatarHandler = () => (isParticipantAvatarErrorLoad.value = true)

const creatorActionsTitle = (status: ActivityStatusType) => {
  return {
    denied: 'rejected this campaign',
    accepted: 'accepted this campaign',
    applied: 'applied for this campaign',
    have_posted: 'posted',
    have_received: 'received product',
    open: 'applied for this campaign',
    suggestion: 'applied for this campaign',
  }[status as string]
}
</script>

<template>
  <FlexComponent :gap="8">
    <ActivityAvatar v-if="activity.type === 'DELETED_COMMENT'" :icon="DeletedIcon" color="#E0E4EA" class="avatar" />
    <ImageComponent
      v-else-if="participant.id === activity.actor_id && participantAvatar && !isParticipantAvatarErrorLoad"
      :image="participantAvatar"
      :width="32"
      :height="32"
      alt="avatar"
      class="avatar"
      @on-load-error="errorParticipantAvatarHandler"
    />
    <ActivityAvatar v-else-if="activity.actor_display_name" :abbreviation="avatarAbbreviation" :color="avatarColor" class="avatar" />
    <ActivityAvatar v-else :icon="SystemIcon" color="#E0E4EA" class="avatar" />
    <div class="activity__content" :class="{ 'activity__content--new': activity.has_unread_notification }">
      <TypographyComponent variant="body-medium" color="#000000">
        <template v-if="activity.actor_id === participant.id">
          <TypographyComponent is="span" variant="label-large" :font-weight="500">
            {{ participant.display_name }}
          </TypographyComponent>
          {{ creatorActionsTitle(activity.new_status) }}
        </template>

        <template v-else>
          <template v-if="activity.actor_display_name">
            <template v-if="activity.new_status === 'store_denied'">
              <TypographyComponent is="span" variant="label-large" :font-weight="500">
                {{ activity.actor_display_name }}
              </TypographyComponent>
              rejected {{ participant.display_name }}
            </template>

            <template v-else-if="activity.type === 'DELETED_COMMENT'">
              {{ activity.actor_display_name }}
              <TypographyComponent is="span" variant="body-medium" :font-weight="bold">deleted</TypographyComponent> comment
            </template>

            <template v-else>
              <TypographyComponent is="span" variant="label-large" :font-weight="500">
                {{ activity.actor_display_name }}
              </TypographyComponent>
              has moved this creator
              <template v-if="activity.old_status">from {{ STATUS_TITLE_MAP[activity.old_status] }}</template>
              to {{ STATUS_TITLE_MAP[activity.new_status] }}
            </template>
          </template>

          <template v-else>
            The status of this creator has been changed
            <template v-if="activity.old_status">from {{ STATUS_TITLE_MAP[activity.old_status] }}</template>
            to {{ STATUS_TITLE_MAP[activity.new_status] }}
          </template>
        </template>
      </TypographyComponent>

      <TypographyComponent variant="body-small" color="#252423" class="date">
        {{ createdDate }}
      </TypographyComponent>
    </div>
  </FlexComponent>
</template>

<style scoped lang="scss">
.avatar {
  flex: 0 0 32px;
  border-radius: 32px;
}

.date {
  position: relative;
  top: -2px;
}

.activity__content {
  position: relative;
}

.activity__content--new {
  padding-right: 20px;
  width: 100%;

  &:before {
    content: '';
    position: absolute;
    right: 2px;
    top: 2px;
    width: 8px;
    height: 8px;
    background: #ea4f85;
    border-radius: 50%;
  }
}
</style>
